import { create } from 'zustand';
import type { LocationStatusItem } from '../types/live';

export interface LocationRealtimeStatus {
  locationId: string;
  isOnline: boolean;
  isAvailable: boolean;
  onlineChargePoints: number;
  totalChargePoints: number;
  availableEvses: number;
  totalEvses: number;
  lastUpdated: number;
}

export type LocationDisplayStatus = 'available' | 'occupied' | 'offline';

interface LocationStatusState {
  statuses: Map<string, LocationRealtimeStatus>;
  lastEventTimestamp: number | null;
  updateLocationStatus: (event: LocationStatusItem) => void;
  getLocationStatus: (locationId: string) => LocationRealtimeStatus | undefined;
  getDisplayStatus: (locationId: string) => LocationDisplayStatus | undefined;
  isLocationAvailable: (locationId: string) => boolean | undefined;
  isLocationOnline: (locationId: string) => boolean | undefined;
  reset: () => void;
}

export function deriveDisplayStatus(status: LocationRealtimeStatus): LocationDisplayStatus {
  if (!status.isOnline) return 'offline';
  return status.isAvailable ? 'available' : 'occupied';
}

export const useLocationStatusStore = create<LocationStatusState>((set, get) => ({
  statuses: new Map(),
  lastEventTimestamp: null,

  updateLocationStatus: (event: LocationStatusItem) => {
    set((state) => {
      const newStatuses = new Map(state.statuses);
      newStatuses.set(event.locationId, {
        locationId: event.locationId,
        isOnline: event.isOnline,
        isAvailable: event.isAvailable,
        onlineChargePoints: event.onlineChargePoints,
        totalChargePoints: event.totalChargePoints,
        availableEvses: event.availableEvses,
        totalEvses: event.totalEvses,
        lastUpdated: Date.now(),
      });
      return { statuses: newStatuses, lastEventTimestamp: Date.now() };
    });
  },

  getLocationStatus: (locationId: string) => get().statuses.get(locationId),

  getDisplayStatus: (locationId: string) => {
    const status = get().statuses.get(locationId);
    if (!status) return undefined;
    return deriveDisplayStatus(status);
  },

  isLocationAvailable: (locationId: string) => get().statuses.get(locationId)?.isAvailable,
  isLocationOnline: (locationId: string) => get().statuses.get(locationId)?.isOnline,

  reset: () => set({ statuses: new Map(), lastEventTimestamp: null }),
}));

export function useLocationRealtimeStatus(locationId: string | null) {
  return useLocationStatusStore((state) => {
    if (!locationId) return null;
    return state.statuses.get(locationId) ?? null;
  });
}

export function useLocationDisplayStatus(locationId: string | null): LocationDisplayStatus | null {
  return useLocationStatusStore((state) => {
    if (!locationId) return null;
    const status = state.statuses.get(locationId);
    if (!status) return null;
    return deriveDisplayStatus(status);
  });
}
